import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Eye, Plus, RefreshCcw } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import PostMovementModal from "./PostMovementModal";
import MovementDetailsModal from "./MovementDetailsModal";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function StockMovements() {
  const { companyId } = useCompany();

  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [warehouseId, setWarehouseId] = useState("");
  const [type, setType] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [openPost, setOpenPost] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;

    const { data: wh } = await supabase
      .from("warehouses")
      .select("id,name,is_default")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    setWarehouses(wh || []);
    const def = (wh || []).find((w) => w.is_default);
    if (!warehouseId && def?.id) setWarehouseId(def.id);

    let q = supabase
      .from("inventory_movements")
      .select(
        "id,movement_type,movement_date,warehouse_id,warehouse_to_id,reference_type,notes,status,posted_to_journal,created_at"
      )
      .eq("company_id", companyId)
      .gte("movement_date", from)
      .lte("movement_date", to)
      .order("movement_date", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(300);

    if (warehouseId) q = q.eq("warehouse_id", warehouseId);
    if (type) q = q.eq("movement_type", type);

    const { data, error } = await q;
    if (!error) setRows(data || []);
  }, [companyId, from, to, type, warehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  const whName = useMemo(() => {
    const map = new Map(warehouses.map((w) => [w.id, w.name]));
    return (id) => map.get(id) || "—";
  }, [warehouses]);

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Stock Movements</h2>
          <p>
            History of inventory postings. Drill down to lines and journals.
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={load}>
            <RefreshCcw size={16} /> Refresh
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={() => setOpenPost(true)}
          >
            <Plus size={16} /> Post Movement
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <div className="fa-toolbar" style={{ marginBottom: 10 }}>
          <select
            className="fa-select"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">All warehouses</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.is_default ? " (Default)" : ""}
              </option>
            ))}
          </select>

          <select
            className="fa-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">All types</option>
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
            <option value="ADJUSTMENT">ADJUSTMENT</option>
            <option value="TRANSFER">TRANSFER</option>
          </select>

          <input
            className="fa-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <input
            className="fa-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />

          <button className="fa-btn" onClick={load}>
            Apply
          </button>
        </div>

        <table className="fa-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Warehouse</th>
              <th>To</th>
              <th>Ref</th>
              <th>Status</th>
              <th>Journals</th>
              <th style={{ width: 120 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id}>
                <td>{m.movement_date}</td>
                <td style={{ fontWeight: 900 }}>{m.movement_type}</td>
                <td>{whName(m.warehouse_id)}</td>
                <td>
                  {m.movement_type === "TRANSFER"
                    ? whName(m.warehouse_to_id)
                    : "—"}
                </td>
                <td>{m.reference_type || "manual"}</td>
                <td>{m.status}</td>
                <td>{m.posted_to_journal ? "Posted" : "—"}</td>
                <td>
                  <button
                    className="fa-btn"
                    onClick={() => {
                      setSelected(m);
                      setOpenDetails(true);
                    }}
                  >
                    <Eye size={16} /> View
                  </button>
                </td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ opacity: 0.75 }}>
                  No movements in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openPost && (
        <PostMovementModal
          onClose={() => setOpenPost(false)}
          onPosted={() => {
            setOpenPost(false);
            load();
          }}
        />
      )}

      {openDetails && selected && (
        <MovementDetailsModal
          movement={selected}
          onClose={() => setOpenDetails(false)}
          warehouseName={whName}
        />
      )}
    </div>
  );
}
